import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { calculateGroupTotal } from "@/hooks/useQuoteEntities";
import { generateUUID } from "@/lib/utils";

export interface QuoteLineItem {
  id: string;
  name: string;
  amount: number;
  factor: number;
  unitPrice: number;
  discount: number;
  total: number;
  notes: string;
  isTitle?: boolean;
  externalNote?: string;
}

export interface QuoteGroup {
  id: string;
  name: string;
  order: number;
  optional: boolean;
  applyProjectDiscount?: boolean;
  items: QuoteLineItem[];
}

export interface QuoteContent {
  groups: QuoteGroup[];
}

export interface QuoteVersion {
  id: string;
  quote_id: string;
  version_label: string;
  version_number: number;
  internal_name: string | null;
  quote_date: string | null;
  valid_until: string;
  validity_days: number;
  is_accepted: boolean;
  pdf_url: string | null;
  project_discount_percent: number;
  vat_rate: "CH_8_1" | "NONE";
  status: "draft" | "sent" | "accepted" | "rejected";
  content: QuoteContent;
  general_conditions: string | null;
  created_at: string;
  updated_at: string;
}

export interface Quote {
  id: string;
  project_id: string;
  name: string;
  quote_number: number;
  customer_id: string | null;
  contact_id: string | null;
  customer?: {
    name: string;
    invoice_address: string | null;
    number: string | null;
  } | null;
  contact?: { first_name: string; last_name: string } | null;
  created_at: string;
  updated_at: string;
  versions?: QuoteVersion[];
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroQuoteVersionRow = {
  id: string;
  quoteID: string;
  versionLabel: string;
  versionNumber: number;
  internalName?: string | null;
  quoteDate?: number | string | null;
  validUntil?: number | string | null;
  validityDays: number;
  isAccepted: boolean;
  pdfURL?: string | null;
  projectDiscountPercent: number;
  vatRate: "CH_8_1" | "NONE";
  content?: QuoteContent | Record<string, unknown> | null;
  deletedAt?: number | string | null;
  status: "draft" | "sent" | "accepted" | "rejected";
  generalConditions?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroQuoteRow = {
  id: string;
  projectID: string;
  name: string;
  quoteNumber?: number | null;
  customerID?: string | null;
  contactID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  versions?: ZeroQuoteVersionRow[] | null;
  customer?: {
    name: string;
    invoiceAddress?: string | null;
    number?: string | null;
  } | null;
  contact?: { firstName: string; lastName: string } | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? null;
}

function toLegacyQuoteVersion(row: ZeroQuoteVersionRow): QuoteVersion {
  return {
    id: row.id,
    quote_id: row.quoteID,
    version_label: row.versionLabel,
    version_number: row.versionNumber,
    internal_name: row.internalName ?? null,
    quote_date: toLegacyDate(row.quoteDate),
    valid_until: toLegacyDate(row.validUntil) ?? "",
    validity_days: row.validityDays,
    is_accepted: row.isAccepted,
    pdf_url: row.pdfURL ?? null,
    project_discount_percent: row.projectDiscountPercent,
    vat_rate: row.vatRate,
    status: row.status,
    content: (row.content as QuoteContent | null) ?? { groups: [] },
    general_conditions: row.generalConditions ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyQuote(row: ZeroQuoteRow): Quote {
  return {
    id: row.id,
    project_id: row.projectID,
    name: row.name,
    quote_number: row.quoteNumber ?? 0,
    customer_id: row.customerID ?? null,
    contact_id: row.contactID ?? null,
    customer: row.customer
      ? {
          name: row.customer.name,
          invoice_address: row.customer.invoiceAddress ?? null,
          number: row.customer.number ?? null,
        }
      : null,
    contact: row.contact
      ? {
          first_name: row.contact.firstName,
          last_name: row.contact.lastName,
        }
      : null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    versions: (row.versions ?? [])
      .filter((version) => !version.deletedAt)
      .map(toLegacyQuoteVersion),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success?: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          if (success) toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useProjectQuotes(projectId: string | undefined) {
  const query = projectId
    ? appQueries.quotes.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const quotes = useMemo(
    () => ((data ?? []) as ZeroQuoteRow[]).map(toLegacyQuote),
    [data],
  );

  return {
    data: quotes,
    isLoading:
      !!projectId && details.type !== "complete" && quotes.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useQuote(quoteId: string | undefined) {
  const query = quoteId ? appQueries.quotes.byID({ id: quoteId }) : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!quoteId });
  const quote = useMemo(
    () => (data ? toLegacyQuote(data as ZeroQuoteRow) : null),
    [data],
  );

  return {
    data: quote,
    isLoading: !!quoteId && details.type !== "complete" && !quote,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateQuote() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<{ projectId: string; name: string }, Quote>({
    success: "Quote created successfully",
    failure: "Failed to create quote",
    run: async ({ projectId, name }) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.quotes.create({ id, projectID: projectId, name }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Quote create rejected");
      return toLegacyQuote({
        id,
        projectID: projectId,
        name,
        quoteNumber: 0,
        versions: [],
      });
    },
  });
}

export function useCopyQuoteFromProject() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      targetProjectId: string;
      sourceQuoteId: string;
      sourceVersionId: string;
    },
    { quoteId: string; versionId: string }
  >({
    success: "Quote copied",
    failure: "Failed to copy quote",
    run: async ({ targetProjectId, sourceQuoteId, sourceVersionId }) => {
      const quoteId = generateUUID();
      const versionId = generateUUID();
      const mutation = zero.mutate(
        appMutators.copyActions.copyQuote({
          targetProjectID: targetProjectId,
          targetQuoteID: quoteId,
          targetVersionID: versionId,
          sourceQuoteID: sourceQuoteId,
          sourceVersionID: sourceVersionId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Quote copy rejected");
      return { quoteId, versionId };
    },
  });
}

export function useUpdateQuote() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      id: string;
      name?: string;
      customer_id?: string | null;
      contact_id?: string | null;
    },
    { id: string }
  >({
    failure: "Failed to update quote",
    run: async ({ customer_id, contact_id, ...updates }) => {
      const mutation = zero.mutate(
        appMutators.quotes.update({
          id: updates.id,
          name: updates.name,
          customerID: customer_id,
          contactID: contact_id,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Quote update rejected");
      return { id: updates.id };
    },
  });
}

export function useDeleteQuote() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    success: "Quote deleted successfully",
    failure: "Failed to delete quote",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.quotes.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Quote delete rejected");
      return id;
    },
  });
}

export function useCreateQuoteVersion() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      quoteId: string;
      versionNumber: number;
      internalName?: string;
      quoteDate?: string;
      validityDays?: number;
      projectDiscountPercent?: number;
      vatRate?: "CH_8_1" | "NONE";
      generalConditions?: string;
    },
    QuoteVersion
  >({
    success: "Version created successfully",
    failure: "Failed to create version",
    run: async ({
      quoteId,
      versionNumber,
      internalName,
      quoteDate,
      validityDays,
      projectDiscountPercent,
      vatRate,
      generalConditions,
    }) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.quoteVersions.create({
          id,
          quoteID: quoteId,
          versionNumber,
          internalName: internalName ?? null,
          quoteDate,
          validityDays,
          projectDiscountPercent,
          vatRate,
          generalConditions: generalConditions ?? null,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version create rejected",
      );
      return toLegacyQuoteVersion({
        id,
        quoteID: quoteId,
        versionLabel: String(versionNumber),
        versionNumber,
        internalName: internalName ?? null,
        quoteDate: quoteDate ?? null,
        validityDays: validityDays ?? 20,
        isAccepted: false,
        projectDiscountPercent: projectDiscountPercent ?? 0,
        vatRate: vatRate ?? "CH_8_1",
        content: { groups: [] },
        status: "draft",
        generalConditions: generalConditions ?? null,
      });
    },
  });
}

export function useUpdateQuoteVersion() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        internal_name: string | null;
        quote_date: string | null;
        valid_until: string;
        validity_days: number;
        is_accepted: boolean;
        pdf_url: string | null;
        project_discount_percent: number;
        vat_rate: "CH_8_1" | "NONE";
        general_conditions: string | null;
        status: "draft" | "sent" | "accepted" | "rejected";
      }>;
    },
    { id: string }
  >({
    failure: "Failed to save version",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.quoteVersions.update({
          id,
          internalName: updates.internal_name,
          quoteDate: updates.quote_date,
          validUntil: updates.valid_until,
          validityDays: updates.validity_days,
          isAccepted: updates.is_accepted,
          pdfURL: updates.pdf_url,
          projectDiscountPercent: updates.project_discount_percent,
          vatRate: updates.vat_rate,
          generalConditions: updates.general_conditions,
          status: updates.status,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteQuoteVersion() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    success: "Version deleted successfully",
    failure: "Failed to delete version",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.quoteVersions.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version delete rejected",
      );
      return id;
    },
  });
}

export function useAcceptQuoteVersion() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, { id: string }>({
    success: "Version accepted",
    failure: "Failed to accept version",
    run: async (id) => {
      const mutation = zero.mutate(
        appMutators.quoteVersions.setStatus({ id, status: "accepted" }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version status rejected",
      );
      return { id };
    },
  });
}

export function useUnacceptQuoteVersion() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, { id: string }>({
    success: "Version acceptance undone",
    failure: "Failed to undo acceptance",
    run: async (id) => {
      const mutation = zero.mutate(
        appMutators.quoteVersions.setStatus({ id, status: "draft" }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version status rejected",
      );
      return { id };
    },
  });
}

export function useSetQuoteVersionStatus() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { id: string; status: "draft" | "sent" | "accepted" | "rejected" },
    { id: string; status: "draft" | "sent" | "accepted" | "rejected" }
  >({
    failure: "Failed to update version status",
    run: async (input) => {
      const mutation = zero.mutate(appMutators.quoteVersions.setStatus(input));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote version status rejected",
      );
      const message =
        input.status === "sent"
          ? "Version marked as sent"
          : input.status === "rejected"
            ? "Version marked as rejected"
            : input.status === "draft"
              ? "Version marked as draft"
              : "Version accepted";
      toast.success(message);
      return input;
    },
  });
}

export function calculateQuoteTotals(
  content: QuoteContent,
  projectDiscountPercent: number,
  vatRate: "CH_8_1" | "NONE",
  deductions: Array<{ description: string; amount: number }> = [],
) {
  let netTotal = 0;
  let optionalTotal = 0;
  let discountEligibleTotal = 0;

  content.groups.forEach((group) => {
    const groupTotal = calculateGroupTotal(group.items);
    if (group.optional) {
      optionalTotal += groupTotal;
    } else {
      netTotal += groupTotal;
      if (group.applyProjectDiscount ?? true) {
        discountEligibleTotal += groupTotal;
      }
    }
  });

  const discountAmount = discountEligibleTotal * (projectDiscountPercent / 100);
  const discountedTotal = netTotal - discountAmount;
  const deductionsTotal = deductions.reduce((sum, d) => sum + d.amount, 0);
  const totalAfterDeductions = discountedTotal - deductionsTotal;
  const vatRateValue = vatRate === "CH_8_1" ? 8.1 : 0;
  const vatAmount = totalAfterDeductions * (vatRateValue / 100);
  const grossTotal = totalAfterDeductions + vatAmount;

  return {
    netTotal,
    optionalTotal,
    discountAmount,
    discountedTotal,
    deductionsTotal,
    totalAfterDeductions,
    vatRateValue,
    vatAmount,
    grossTotal,
  };
}

export function getNextVersionNumber(quote: Quote): number {
  return (
    (quote.versions ?? []).reduce(
      (max, version) => Math.max(max, version.version_number),
      0,
    ) + 1
  );
}
